// ----------------------------------------------------------------------
// <copyright file="Izenda.SubscribedInstancesStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

!(function() {
  var m = require('mobx');
  var ReportFetcher = require('fetchers/Izenda.ReportsDataFetcher');
  var DashboardFetcher = require('fetchers/Izenda.DashboardDataFetcher');
  var SubscribedInstances = require('./models/Izenda.SubscribedInstances');
  var ObjectStateConstants = require('logic/data/Izenda.ObjectStateConstants');
  var SubscriptionDetails = require('logic/models/Izenda.SubscriptionDetails');
  var MobxUtils = require('share/utils/Izenda.MobxUtils');
  var SystemVariables = require('logic/models/Izenda.SystemVariables');
  var systemSettingFetcher = require('fetchers/Izenda.SystemSettingFetcher');

  var REPORTING_TYPE = {
    REPORT: 1,
    DASHBOARD: 2
  };
  var TEMPLATE_TYPE = {
    SCHEDULE: 0,
    SUBSCRIPTION: 1
  };
  var uiState = require('logic/Izenda.UiState.js');
  var { isNil, map, cloneDeep, placeholder } = require('lodash/fp');

  function SubscribedInstancesStore(isDashboard, store) {
    this.fetcher = !isNil(isDashboard) && isDashboard ? DashboardFetcher : ReportFetcher;

    var props = {
      model: new SubscribedInstances(),
      systemVariables: new SystemVariables(),
      reportId: null,
      dashboardId: null,

      activeInstances: function() {
        return this.model.loadActiveInstances();
      },

      isSubscriptionMode: function() {
        return this.model.isSubscriptionMode;
      },
      tenantId: function() {
        return uiState.getTenantId();
      },
      templateType: function() {
        return this.isSubscriptionMode ? TEMPLATE_TYPE.SUBSCRIPTION : TEMPLATE_TYPE.SCHEDULE;
      }
    };

    m.extendObservable(this, props);
    store && this.setObserveSubscriptions(store);
    
  }

  /**
   * Load system variables
   */
  SubscribedInstancesStore.prototype.fetchSystemVariables = m.action(function() {
    var reportingType = !isNil(this.reportId) ? REPORTING_TYPE.REPORT : REPORTING_TYPE.DASHBOARD;
    this.systemVariables.pageInfo.buildPagingRequest();

    return systemSettingFetcher
      .loadEmailVariables(reportingType, this.systemVariables.pageInfo)
      .bind(this)
      .then(function(data) {
        return this.systemVariables.populateData(data);
      });
  });

  /**
   * Load Instances
   */
  SubscribedInstancesStore.prototype.updateReportId = m.action(function(reportId) {
    this.model.resetPageModel();
    this.reportId = reportId;
  });

  /**
   * Reset store model
   * @param {String} dashboard id
   */
  SubscribedInstancesStore.prototype.updateDashboardId = m.action(function(dashboardId, isSubscriptionMode) {
    if (this.dashboardId !== dashboardId) {
      this.model = new SubscribedInstances();
      this.model.isSubscriptionMode = isSubscriptionMode;
      this.dashboardId = dashboardId;
    }
  });
 

  /**
   * Load Instances
   */
  SubscribedInstancesStore.prototype.loadInstances = m.action(function(subscribedInstances) {
    // TODO: tenantId will be update later
    var model = this.model;

    if (subscribedInstances && subscribedInstances.length > 0) {
      model.populateData({
        result: subscribedInstances,
        total: subscribedInstances.length
      });

      return this.fetcher.fetchEmailTemplates(this.templateType).then(
        m.action(function(emailTemplates) {
          model.populateEmailTemplates(emailTemplates);
          return Promise.resolve(true);
        })
      );
    }

    return this.loadInstancesWithEmailTemplates();
  });

  /**
   * Load Instances with email templates.
   */
  SubscribedInstancesStore.prototype.loadInstancesWithEmailTemplates = m.action(function() {
    // TODO: tenantId will be update later
    var model = this.model;
    model.pageInfo.buildPagingRequest();

    return Promise.join(
      this.fetcher.loadSubscription(
        this.tenantId,
        model.pageInfo,
        this.reportId || this.dashboardId,
        this.isSubscriptionMode
      ),
      this.fetcher.fetchEmailTemplates(this.templateType),

      m.action(function(instances, emailTemplates) {
        model.populateData(instances);
        model.populateEmailTemplates(emailTemplates);
        return Promise.resolve(true);
      })
    );
  });

  /**
   * Save Subscribed Instance(s)
   */
  SubscribedInstancesStore.prototype.saveInstances = function() {
    var subscribedInstances = map(function(item) {
      return item.omitData();
    }, this.model.buildDataForSaving());
    return this.fetcher.updateSubscriptions(subscribedInstances);
  };

  /**
   * Handle when page size change
   */
  SubscribedInstancesStore.prototype.onPageSizeChange = function(pageSize) {
    this.model.pageInfo.setPageSize(pageSize);
    return this.goToPage(1);
  };

  /**
   * Handle go to page
   * @param  {int} pageIndex
   */
  SubscribedInstancesStore.prototype.goToPage = function(pageIndex) {
    var pageInfo = this.model.pageInfo;
    pageInfo.setPageIndex(pageIndex);
    return this.loadInstances();
  };

  /**
   * Search subscription
   * @param  {string} searchProperty
   * @param  {string} searchText
   */
  SubscribedInstancesStore.prototype.search = function(searchProperty, searchText) {
    this.model.pageInfo.setSearchValues(searchProperty, searchText);
    return this.goToPage(1);
  };

  /**
   * Change sort orders
   * @param {string} columnKey
   * @param {object} sortDir
   */
  SubscribedInstancesStore.prototype.onSortChange = function(columnKey, sortDir) {
    this.model.pageInfo.setColSortDirs(columnKey, sortDir);
    return this.loadInstances();
  };

  /**
   * Sort system variables
   * @param  {String} columnKey
   * @param  {String} sortDir
   */
  SubscribedInstancesStore.prototype.sortSystemVariables = m.action(function(columnKey, sortDir) {
    this.systemVariables.pageInfo.setColSortDirs(columnKey, sortDir);
    return this.fetchSystemVariables();
  });

  /**
   * Handle when page size of system variables change
   * @param  {Number} pageSize
   */
  SubscribedInstancesStore.prototype.onSystemVariablesPageSizeChange = m.action(function(pageSize) {
    this.systemVariables.pageInfo.setPageSize(pageSize);
    return this.fetchSystemVariables();
  });

  /**
   * Handle go to page of system variables.
   * @param  {Number} pageIndex
   */
  SubscribedInstancesStore.prototype.goToSystemVariablesPage = m.action(function(pageIndex) {
    this.model.systemVariables.setPageIndex(pageIndex);
    return this.fetchSystemVariables();
  });

  /**
   * Update filter criteria information.
   * @param {String} searchProperty
   * @param {String} searchText
   */
  SubscribedInstancesStore.prototype.updateSearchCriteria = function(searchProperty, searchText) {
    this.model.pageInfo.setSearchValues(searchProperty, searchText);
  };

  /**
   * Copy a subscribed instances by its id.
   * @param {String} id
   */
  SubscribedInstancesStore.prototype.copySubscribedInstance = function(id) {
    this.model.copySubscribedInstance(id);
  };

  /**
   * Delete selected instance.
   * @param {Array} ids
   */
  SubscribedInstancesStore.prototype.removeSubscribedInstances = function(ids) {
    this.model.removeSubscribedInstances(ids);
  };
  /**
   * Load email templates
   */
  SubscribedInstancesStore.prototype.fetchEmailTemplates = function() {
    return this.fetcher.fetchEmailTemplates(this.templateType).then(
      function(result) {
        return this.model.populateEmailTemplates(result);
      }.bind(this)
    );
  };

  /**
   * Validate subscription instance.
   * @param {Object} instance
   * @param {Array} commonFilterFields
   * @returns {Promise}
   */
  SubscribedInstancesStore.prototype.validateSubscription = function(instance, commonFilterFields) {
    var clonedInstance = new SubscriptionDetails(cloneDeep(instance));
    clonedInstance.buildRecurrencePatternSetting();
    clonedInstance.subscriptionFilterFields =
      instance.subscriptionFilterFields && cloneDeep(MobxUtils.toJS(instance.subscriptionFilterFields));
    clonedInstance.subscriptionCommonFilterFields =
      instance.subscriptionCommonFilterFields && cloneDeep(MobxUtils.toJS(instance.subscriptionCommonFilterFields));

    if (clonedInstance.state === ObjectStateConstants.INSERT) {
      clonedInstance.id = null;
    }

    clonedInstance.dashboardId = this.dashboardId;

    return this.fetcher.validateSubscription(clonedInstance.omitData(), commonFilterFields);
  };

  /**
   * Set observe subscriptions.
   */
  SubscribedInstancesStore.prototype.setObserveSubscriptions = function(store) {
    this.disposeSubscriptions = m.observe(
      store.model.dashboardDetail.dashboard,
      'id',
      function() {
        this.model.subscribedInstances = map(function(item) {
          return new SubscriptionDetails(item);
        }, store.model.dashboardDetail.dashboard.subscriptions);
      }.bind(this)
    );
  };

  /**
   * Dispose observe subscriptions.
   */
  SubscribedInstancesStore.prototype.disposeObserveSubscriptions = function() {
    this.disposeSubscriptions();
  };

  /**
   * Load Schedules
   * @param {Boolean} isNew
   * @param {Object} subscriptions
   * @returns {Object} Promise
   */
  SubscribedInstancesStore.prototype.loadSchedules = function(isNew, subscriptions) {
    var model = this.model;
    model.pageInfo.buildPagingRequest();

    return Promise.join(
      this.fetcher.loadSchedules(this.reportId, subscriptions, model.pageInfo),
      this.fetcher.fetchEmailTemplates(this.templateType),

      function(instances, emailTemplates) {
        m.transaction(function() {
          model.populateData(instances);
          model.updateScheduleInstances(isNew);
          model.populateEmailTemplates(emailTemplates);
        });
        return Promise.resolve(true);
      }
    );
  };

  /**
   * Search schedules
   * @param  {string} searchProperty
   * @param  {string} searchText
   */
  SubscribedInstancesStore.prototype.searchSchedules = function(searchProperty, searchText) {
    this.model.pageInfo.setSearchValues(searchProperty, searchText);
    var pageInfo = this.model.pageInfo;
    pageInfo.setPageIndex(1);

    var schedules = placeholder(this.model.scheduleInstances)
      .chain()
      .map(function(item) {
        return new SubscriptionDetails(item);
      })
      .filter(function(item) {
        return item.state !== ObjectStateConstants.NONE;
      })
      .forEach(function(i) {
        if (i.state === ObjectStateConstants.INSERT) {
          i.id = null;
        }
      })
      .value();

    return this.loadSchedules(false, schedules);
  };

  /**
   * Clean Subcription model
   */
  SubscribedInstancesStore.prototype.cleanSubscription = function() {
    this.model = new SubscribedInstances();
  };

  module.exports = SubscribedInstancesStore;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.SubscribedInstancesStore.js